import { useEffect, useState, type FC } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'
import { Select } from '@wartek-id/select'

import useRiwayatTahunanAccessibility from 'app/KinerjaShared/hooks/useRiwayatTahunanAccessibility'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { Card } from 'app/KinerjaShared/components/Card'
import FilterButtons from 'app/KinerjaShared/components/FilterButtons'
import { RoleLabelShort } from 'app/KinerjaKSPage/utils/profile'
import { PENGELOLAAN_KINERJA_RIWAYAT_PATH } from 'app/KinerjaShared/constants/path'
import useSKPRiwayatTrackEvent from 'app/KinerjaRiwayat/hooks/useSKPRiwayatTrackEvent'
import useToggleSdkContext from 'utils/useToogleSdk'
import RiwayatTahunan from 'app/KinerjaRiwayat/components/RiwayatTahunan'
import RiwayatPeriode from 'app/KinerjaRiwayat/components/RiwayatPeriode'

import type { KinerjaBaseRoleType } from 'types/kinerja'
import type { PageProps } from 'app/KinerjaShared/components/KinerjaLayout'

type TRiwayatProps = PageProps & {}

export type TFilterItem = 'ALL' | KinerjaBaseRoleType

type TFilterItemObj = {
  label: string
  value: TFilterItem[]
}

type TFilterItems = TFilterItemObj[]

const today = new Date() // Get the current date
const currentYear = today.getFullYear() // Get the current year to be used as the base year
const rowHeaderClasses = 'overflow-wrap-anywhere align-middle'
const rowItemClasses = 'overflow-wrap-anywhere align-middle'

const filterStateLabel = (state: TFilterItem) => {
  return RoleLabelShort[state] || 'Semua'
}

const filterItems: TFilterItems = [
  { value: ['ALL'] },
  { value: ['GURU'] },
  { value: ['KS_DEFINITIF'] },
  { value: ['KS_PLT'] },
].map((item: TFilterItemObj) => {
  const label = filterStateLabel(item?.value?.[0])
  return {
    ...item,
    label,
  }
})

const trackerFilter: Record<TFilterItem, string> = {
  ALL: 'semua',
  GURU: 'guru',
  KS_DEFINITIF: 'KS',
  KS_PLT: 'KS Plt',
}

/**
 * Returns a list of years starting from 2024 up to the current year.
 *
 * @returns {number[]} An array of years from 2024 to the current year.
 */
const getYearsList = (): number[] => {
  const startYear = 2024
  const years: number[] = []

  for (let year = startYear; year <= currentYear; year++) {
    years.push(year)
  }

  return years
}

const Riwayat: FC<TRiwayatProps> = ({}) => {
  const router = useRouter()

  const toggleValue = useToggleSdkContext()
  const { trackEvent } = useSKPRiwayatTrackEvent()

  const [activeFilter, setActiveFilter] = useState<TFilterItem[]>(null)
  const [activeYear, setActiveYear] = useState<string>(`${currentYear}`)
  const [isTahunanLoading, setIsTahunanLoading] = useState<boolean>(false)
  const [isPeriodeLoading, setIsPeriodeLoading] = useState<boolean>(false)

  const showVersion2 =
    !!toggleValue['kinerja-ks-dokumen-evaluasi-revamp-enabled']
  const showVersion2Tahunan =
    !!toggleValue['kinerja-ks-dokumen-evaluasi-tahunan-enabled']

  const { isLoading: isLoadingGetCommonConfig, isAfterAllowedDate } =
    useRiwayatTahunanAccessibility({
      today,
      contextYear: parseInt(activeYear, 10),
    })

  const isLoading =
    isTahunanLoading || isPeriodeLoading || isLoadingGetCommonConfig

  // Update URL query params when activeFilter or activeYear changes
  useEffect(() => {
    if (router.isReady && !!activeFilter && !!activeYear)
      router.push(
        `${PENGELOLAAN_KINERJA_RIWAYAT_PATH}?year=${activeYear}&filter=${activeFilter}`,
        null
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, activeYear])

  // Get query params from URL, and set activeFilter and activeYear on initial load
  useEffect(() => {
    if (router.isReady && !isLoading) {
      const { year, filter } = router.query

      // If year is set, set it as activeYear
      if (year && getYearsList().includes(parseInt(`${year}`, 10)))
        setActiveYear(year as string)

      // If filter is set and the value is within the filterItems, set it as activeFilter
      if (
        filter &&
        filterItems
          .flatMap((item) => item.value)
          .includes(filter as TFilterItem)
      ) {
        setActiveFilter([filter] as TFilterItem[])
      }

      // Else, set activeFilter to ALL
      else {
        setActiveFilter(['ALL'])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, isLoading])

  // Send tracker upon mounted
  useEffect(() => {
    trackEvent({
      activity_type: 'open_riwayat_kinerja',
      screen_name: 'beranda_riwayat_kinerja',
      event_trigger: 'beranda_riwayat_kinerja',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu="riwayat" />

      <main className="p-6">
        <Card
          className={clsx('p-6 rounded-lg bg-white', {
            'space-y-6': !showVersion2,
            'flex flex-col gap-12': showVersion2,
          })}
        >
          <section className="space-y-6">
            {/* Title */}
            <Text variant="display-md">Riwayat Pengelolaan Kinerja</Text>

            {/* Dropdown */}
            <div className="w-80 flex flex-col gap-1">
              <Text variant="body">Pilih tahun</Text>
              <Select
                disabled={isLoading}
                value={activeYear}
                onChange={(e) => setActiveYear(e.target.value)}
              >
                {getYearsList().map((year, index) => (
                  <option key={`option-${index}`} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>

            {/* Filter */}
            <FilterButtons<TFilterItem[]>
              items={filterItems}
              activeValue={activeFilter}
              btnSize="md"
              disabled={isLoading}
              onChange={(value) => setActiveFilter(value)}
            />
          </section>

          {showVersion2 && showVersion2Tahunan && isAfterAllowedDate && (
            <RiwayatTahunan
              activeFilter={activeFilter}
              activeYear={activeYear}
              rowHeaderClasses={rowHeaderClasses}
              rowItemClasses={rowItemClasses}
              onLoading={setIsTahunanLoading}
            />
          )}

          <RiwayatPeriode
            showVersion2={showVersion2}
            activeFilter={activeFilter}
            activeYear={activeYear}
            rowHeaderClasses={rowHeaderClasses}
            rowItemClasses={rowItemClasses}
            trackerFilter={trackerFilter}
            onLoading={setIsPeriodeLoading}
            isLoading={isLoading}
          />
        </Card>
      </main>
    </div>
  )
}

export default Riwayat
